import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { useAuthStore } from './auth.store';

// Organization store interfaces
interface OrgSettings {
  theme?: string;
  branding?: {
    logo?: string;
    primaryColor?: string;
    secondaryColor?: string;
    backgroundImage?: string;
  };
  features?: string[];
  defaultModules?: string[];
}

interface Organization {
  id: string;
  name: string;
  slug: string;
  plan: string;
  settings: OrgSettings;
  createdAt: string;
}

interface OrgState {
  // State
  organizationData: Organization | null;
  members: Array<{ id: string; name: string; email: string; role: string }>;
  isLoading: boolean;
  error: string | null;

  // Actions
  fetchOrganizationData: () => Promise<void>;
  fetchOrgMembers: () => Promise<void>;
  updateOrgSettings: (settings: Partial<OrgSettings>) => Promise<void>;
  setOrganization: (org: Organization) => void;
  reset: () => void;
}

// Default organization data (for development/testing)
const defaultOrganization: Organization = {
  id: '6bc87e30-7015-4ae1-9ed6-1e7290163ee2',
  name: 'Nexgen Organization',
  slug: 'nexgen',
  plan: 'enterprise',
  settings: {
    theme: 'light',
    branding: {
      logo: 'https://nexgen-dev-org-files-public.s3.us-east-1.amazonaws.com/app/defaults/logo.png',
      primaryColor: '#1a73e8',
      secondaryColor: '#90887e',
      backgroundImage:
        'https://nexgen-dev-org-files-public.s3.us-east-1.amazonaws.com/app/defaults/welcome-bg.jpg',
    },
    features: ['bookmarks', 'calendar', 'tasks'],
    defaultModules: ['bookmarks', 'calendar'],
  },
  createdAt: '2023-01-01',
};

export const useOrgStore = create<OrgState>()(
  persist(
    (set, get) => ({
      organizationData: null,
      members: [],
      isLoading: false,
      error: null,
      hasActiveSubscription: true,

      fetchOrganizationData: async () => {
        const token = await useAuthStore.getState().getToken();
        const userData = useAuthStore.getState().user;

        if (!token || !userData?.organizationId) {
          set({ error: 'No authentication token or organization ID available' });
          return;
        }

        set({ isLoading: true, error: null });

        try {
          // This would be your actual API call in production
          // const response = await fetch(`/api/organizations/${userData.organizationId}`, {
          //   headers: { Authorization: `Bearer ${token}` }
          // });

          // if (!response.ok) {
          //   throw new Error(`Failed to fetch organization data: ${response.statusText}`);
          // }

          // const orgData = await response.json();

          // For development, use the default organization data
          // In production, you would use the API response
          const orgData = defaultOrganization;

          set({
            organizationData: orgData,
            isLoading: false,
          });

          console.log('Organization data loaded:', orgData.name);
        } catch (error) {
          console.error('Failed to fetch organization data:', error);
          set({
            error: error instanceof Error ? error.message : 'Unknown error occurred',
            isLoading: false,
          });
        }
      },

      fetchOrgMembers: async () => {
        const token = await useAuthStore.getState().getToken();
        const orgData = get().organizationData;

        if (!token || !orgData) {
          set({ error: 'No authentication token or organization data available' });
          return;
        }

        set({ isLoading: true, error: null });

        try {
          // This would be your actual API call in production
          // const response = await fetch(`/api/organizations/${orgData.id}/members`, {
          //   headers: { Authorization: `Bearer ${token}` }
          // });

          // if (!response.ok) {
          //   throw new Error(`Failed to fetch organization members: ${response.statusText}`);
          // }

          // const members = await response.json();

          // For development, use mock members data
          const members = [
            { id: 'member1', name: 'John Doe', email: 'john@example.com', role: 'admin' },
            { id: 'member2', name: 'Jane Smith', email: 'jane@example.com', role: 'member' },
          ];

          set({
            members,
            isLoading: false,
          });
        } catch (error) {
          console.error('Failed to fetch organization members:', error);
          set({
            error: error instanceof Error ? error.message : 'Unknown error occurred',
            isLoading: false,
          });
        }
      },

      updateOrgSettings: async (settings: Partial<OrgSettings>) => {
        const token = await useAuthStore.getState().getToken();
        const orgData = get().organizationData;

        if (!token || !orgData) {
          set({ error: 'No authentication token or organization data available' });
          return;
        }

        set({ isLoading: true, error: null });

        try {
          // This would be your actual API call in production
          // const response = await fetch(`/api/organizations/${orgData.id}/settings`, {
          //   method: 'PATCH',
          //   headers: {
          //     'Content-Type': 'application/json',
          //     Authorization: `Bearer ${token}`
          //   },
          //   body: JSON.stringify(settings)
          // });

          // if (!response.ok) {
          //   throw new Error(`Failed to update organization settings: ${response.statusText}`);
          // }

          // const updatedSettings = await response.json();

          // For development, just update the local state
          const updatedOrg = {
            ...orgData,
            settings: {
              ...orgData.settings,
              ...settings,
            },
          };

          set({
            organizationData: updatedOrg,
            isLoading: false,
          });

          console.log('Organization settings updated');
        } catch (error) {
          console.error('Failed to update organization settings:', error);
          set({
            error: error instanceof Error ? error.message : 'Unknown error occurred',
            isLoading: false,
          });
        }
      },

      setOrganization: (org: Organization) => {
        set({ organizationData: org });
      },

      reset: () => {
        set({
          organizationData: null,
          members: [],
          isLoading: false,
          error: null,
        });
      },
    }),
    {
      name: 'org-storage',
      partialize: (state) => ({
        // Only persist organization ID and basic info, not the full data
        // which should be re-fetched on initialization
        organizationData: state.organizationData
          ? {
              id: state.organizationData.id,
              name: state.organizationData.name,
              slug: state.organizationData.slug,
            }
          : null,
      }),
    }
  )
);
